'use client';

import { useEffect, useState, useRef, useCallback } from 'react';
import { isTauri, isCapacitor, isWeb, getPlatform } from '@/lib/platformUtils';
import { authService } from '@/lib/authService';
import { buildApiUrl } from '@/lib/platformUtils';
import { isTotalConnectDesktopWindow, isTotalConnectStandaloneWindowLabel } from '@/lib/desktopAppVariant';

// ============================================
// Type Definitions
// ============================================

export type SipRegistrationStatus =
  | 'unregistered'
  | 'registering'
  | 'registered'
  | 'failed'
  | 'unknown';

export type ConnectionQuality =
  | 'excellent'
  | 'good'
  | 'fair'
  | 'poor'
  | 'unknown';

export interface MediaDeviceState {
  audioInputs: MediaDeviceInfo[];
  audioOutputs: MediaDeviceInfo[];
  videoInputs: MediaDeviceInfo[];
  selectedAudioInput: string | null;
  selectedAudioOutput: string | null;
  selectedVideoInput: string | null;
}

export interface ConnectionStats {
  quality: ConnectionQuality;
  latencyMs: number | null;
  jitterMs: number | null;
  packetLossPercent: number | null;
  bandwidthKbps: number | null;
  iceConnectionState: RTCIceConnectionState | null;
  lastUpdated: number | null;
}

export interface SipRegistrationState {
  status: SipRegistrationStatus;
  extension: string | null;
  server: string | null;
  lastRegistered: number | null;
  error: string | null;
}

export interface DesktopWindowState {
  isFocused: boolean;
  isMinimized: boolean;
  isFullscreen: boolean;
  isAlwaysOnTop: boolean;
}

export type LiveKitRoomStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'unknown';

export interface LiveKitRoomState {
  status: LiveKitRoomStatus;
  roomName: string | null;
  participantCount: number;
  serverUrl: string | null;
  error: string | null;
}

export type SipChannelState = 'idle' | 'ringing' | 'answered' | 'held' | 'bridged' | 'unknown';

export interface SipChannelStatus {
  state: SipChannelState;
  channelId: string | null;
  callerNumber: string | null;
  calleeNumber: string | null;
  direction: 'inbound' | 'outbound' | null;
  bridgedTo: string | null;
}

export type PresenceStatus = 'online' | 'away' | 'busy' | 'dnd' | 'offline';

export interface MultiWindowEvent {
  type: 'incoming-call' | 'call-ended' | 'sip-registered' | 'sip-failed' | 'presence-changed' | 'device-changed';
  payload: Record<string, unknown>;
  sourceWindow: string;
  timestamp: number;
}

export interface TotalConnectStandaloneState {
  /** True when this webview is the Tauri standalone Total Connect window */
  isStandalone: boolean;
  /** The Tauri window label, null if not Tauri */
  windowLabel: string | null;
  /** Whether running in Tauri desktop app */
  isTauriDesktop: boolean;
  /** Whether running in Capacitor mobile app */
  isCapacitorMobile: boolean;
  /** Whether running in web browser */
  isWebBrowser: boolean;
  /** Current platform name */
  platform: string;

  /** SIP registration state */
  sipRegistration: SipRegistrationState;
  /** Whether SIP registration check is in-flight */
  sipRegistrationLoading: boolean;

  /** Media device state */
  mediaDevices: MediaDeviceState;
  /** Whether device enumeration is in progress */
  mediaDevicesLoading: boolean;

  /** Connection quality stats */
  connectionStats: ConnectionStats;

  /** Desktop window state (Tauri only) */
  desktopWindow: DesktopWindowState;

  /** Whether call mode is active (always-on-top + prevent sleep) */
  isCallModeActive: boolean;

  /** LiveKit room connection state */
  livekitRoom: LiveKitRoomState;

  /** SIP channel state */
  sipChannel: SipChannelStatus;

  /** User presence status */
  presence: PresenceStatus;

  // ── Actions ──

  /** Enable call mode: always-on-top + prevent display sleep */
  enableCallMode: () => Promise<void>;
  /** Disable call mode: restore normal window behavior */
  disableCallMode: () => Promise<void>;
  /** Focus and raise the window (for incoming calls) */
  focusWindow: () => Promise<void>;
  /** Refresh media device enumeration */
  refreshMediaDevices: () => Promise<void>;
  /** Set the active audio input device */
  setAudioInputDevice: (deviceId: string) => Promise<void>;
  /** Set the active audio output device */
  setAudioOutputDevice: (deviceId: string) => Promise<void>;
  /** Check SIP registration status from the server */
  checkSipRegistration: () => Promise<void>;
  /** Start polling connection quality stats (requires RTCPeerConnection) */
  startConnectionMonitoring: (pc: RTCPeerConnection) => void;
  /** Stop polling connection quality stats */
  stopConnectionMonitoring: () => void;
  /** Monitor a LiveKit room connection */
  monitorLiveKitRoom: (room: { name: string; state: string; numParticipants: number; serverUrl?: string }, error?: string | null) => void;
  /** Update SIP channel state from external events */
  updateSipChannel: (status: Partial<SipChannelStatus>) => void;
  /** Set user presence status */
  setPresence: (status: PresenceStatus) => void;
  /** Broadcast an event to other Tauri windows */
  broadcastWindowEvent: (type: MultiWindowEvent['type'], payload?: Record<string, unknown>) => Promise<void>;
  /** Switch audio output device (speaker/headset) */
  setAudioOutputSink: (deviceId: string) => Promise<void>;
}

// ============================================
// Internal Helpers
// ============================================

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as Record<string, unknown>).message ?? '');
  }
  return '';
}

function isAclDeniedWindowCommand(error: unknown): boolean {
  return getErrorMessage(error).includes('not allowed by ACL');
}

async function getCurrentTauriWindow() {
  const { getCurrentWindow } = await import('@tauri-apps/api/window');
  return getCurrentWindow();
}

async function tauriInvoke<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  const tauri = (window as unknown as { __TAURI_INTERNALS__?: { invoke: (cmd: string, args?: Record<string, unknown>) => Promise<T> } }).__TAURI_INTERNALS__;
  if (!tauri) throw new Error('Tauri internals not available');
  return tauri.invoke(command, args);
}

// ============================================
// Connection Quality Analysis
// ============================================

function analyzeConnectionQuality(stats: {
  latencyMs: number;
  jitterMs: number;
  packetLossPercent: number;
  bandwidthKbps: number;
}): ConnectionQuality {
  const { latencyMs, jitterMs, packetLossPercent } = stats;

  if (packetLossPercent > 5 || latencyMs > 500 || jitterMs > 50) {
    return 'poor';
  }
  if (packetLossPercent > 2 || latencyMs > 200 || jitterMs > 30) {
    return 'fair';
  }
  if (packetLossPercent > 0.5 || latencyMs > 100 || jitterMs > 15) {
    return 'good';
  }
  return 'excellent';
}

// ============================================
// Hook
// ============================================

/**
 * Comprehensive Total Connect standalone window hook — the foundation for a
 * white-label UCaaS softphone comparable to Tragofone.
 *
 * Covers the full RTC / SIP / LiveKit / SIP integration surface:
 * - Tauri standalone window detection
 * - Desktop window management (always-on-top, focus, sleep prevention)
 * - SIP registration status monitoring
 * - Media device enumeration, selection, and output routing
 * - WebRTC connection quality monitoring (latency, jitter, packet loss)
 * - LiveKit room connection state
 * - SIP channel state
 * - Multi-window event coordination (Tauri event bus)
 * - User presence (online / away / busy / dnd)
 * - Multi-platform awareness (Tauri desktop, Capacitor mobile, web)
 *
 * Usage:
 * ```ts
 * const tc = useTotalConnectStandaloneWindow();
 * if (tc.isStandalone) { ... }
 * await tc.enableCallMode();
 * await tc.checkSipRegistration();
 * tc.monitorLiveKitRoom(room);
 * tc.setPresence('busy');
 * ```
 */
export function useTotalConnectStandaloneWindow(): TotalConnectStandaloneState {
  // ── Window detection ──
  const [isStandalone, setIsStandalone] = useState(() => isTotalConnectDesktopWindow());
  const [windowLabel, setWindowLabel] = useState<string | null>(null);

  const isTauriDesktop = isTauri();
  const isCapacitorMobile = isCapacitor();
  const isWebBrowser = isWeb();
  const platform = getPlatform();

  // ── Desktop window state ──
  const [desktopWindow, setDesktopWindow] = useState<DesktopWindowState>({
    isFocused: true,
    isMinimized: false,
    isFullscreen: false,
    isAlwaysOnTop: false,
  });

  // ── Call mode ──
  const [isCallModeActive, setIsCallModeActive] = useState(false);
  const callModeActiveRef = useRef(false);
  const windowControlsBlockedRef = useRef(false);

  // ── SIP registration ──
  const [sipRegistration, setSipRegistration] = useState<SipRegistrationState>({
    status: 'unknown',
    extension: null,
    server: null,
    lastRegistered: null,
    error: null,
  });
  const [sipRegistrationLoading, setSipRegistrationLoading] = useState(false);

  // ── Media devices ──
  const [mediaDevices, setMediaDevices] = useState<MediaDeviceState>({
    audioInputs: [],
    audioOutputs: [],
    videoInputs: [],
    selectedAudioInput: null,
    selectedAudioOutput: null,
    selectedVideoInput: null,
  });
  const [mediaDevicesLoading, setMediaDevicesLoading] = useState(false);

  // ── Connection stats ──
  const [connectionStats, setConnectionStats] = useState<ConnectionStats>({
    quality: 'unknown',
    latencyMs: null,
    jitterMs: null,
    packetLossPercent: null,
    bandwidthKbps: null,
    iceConnectionState: null,
    lastUpdated: null,
  });
  const connectionMonitorRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);

  // ── LiveKit room state ──
  const [livekitRoom, setLivekitRoom] = useState<LiveKitRoomState>({
    status: 'unknown',
    roomName: null,
    participantCount: 0,
    serverUrl: null,
    error: null,
  });

  // ── SIP channel state ──
  const [sipChannel, setSipChannel] = useState<SipChannelStatus>({
    state: 'unknown',
    channelId: null,
    callerNumber: null,
    calleeNumber: null,
    direction: null,
    bridgedTo: null,
  });

  // ── Presence ──
  const [presence, setPresenceState] = useState<PresenceStatus>('online');

  // ── Window detection effect ──
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (
        typeof window === 'undefined' ||
        !(window as unknown as { __TAURI_INTERNALS__?: unknown }).__TAURI_INTERNALS__
      ) {
        return;
      }
      try {
        const w = await getCurrentTauriWindow();
        if (cancelled) return;
        setWindowLabel(w.label);
        if (isTotalConnectStandaloneWindowLabel(w.label) || isTotalConnectDesktopWindow()) {
          setIsStandalone(true);
        }
      } catch {
        // Not Tauri or API unavailable
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Desktop window state effect (Tauri only) ──
  useEffect(() => {
    if (!isTauriDesktop) return;

    let offFocus: (() => void) | undefined;
    let cancelled = false;

    void (async () => {
      try {
        const w = await getCurrentTauriWindow();
        if (cancelled) return;

        setDesktopWindow((prev) => ({
          ...prev,
          isFocused: true, // assume focused on mount
          isFullscreen: false,
        }));

        offFocus = await w.onFocusChanged((event: { payload: boolean }) => {
          setDesktopWindow((prev) => ({ ...prev, isFocused: event.payload }));
        });

        // Poll for minimized state since Tauri v1 doesn't emit minimize events directly
        const interval = setInterval(async () => {
          try {
            const minimized = await w.isMinimized();
            setDesktopWindow((prev) =>
              prev.isMinimized !== minimized ? { ...prev, isMinimized: minimized } : prev,
            );
          } catch {
            // ignore
          }
        }, 2000);

        return () => clearInterval(interval);
      } catch {
        // Window API unavailable
      }
    })();

    return () => {
      cancelled = true;
      offFocus?.();
    };
  }, [isTauriDesktop]);

  // ── ACL error handler ──
  const handleWindowAclError = useCallback((action: string, error: unknown): boolean => {
    if (!isAclDeniedWindowCommand(error)) return false;
    if (!windowControlsBlockedRef.current) {
      windowControlsBlockedRef.current = true;
      console.warn(
        `[useTotalConnectStandaloneWindow] Window control blocked by ACL during ${action}; ` +
          'using sleep-prevention only until the desktop app is rebuilt with updated permissions.',
        error,
      );
    }
    return true;
  }, []);

  // ── enableCallMode ──
  const enableCallMode = useCallback(async () => {
    if (!isTauriDesktop) return;
    if (callModeActiveRef.current) return;

    try {
      if (!windowControlsBlockedRef.current) {
        const tauriWindow = await getCurrentTauriWindow();
        try {
          await tauriWindow.setAlwaysOnTop(true);
          setDesktopWindow((prev) => ({ ...prev, isAlwaysOnTop: true }));
        } catch (error) {
          if (!handleWindowAclError('enableCallMode', error)) throw error;
        }
      }

      await tauriInvoke('prevent_display_sleep', { prevent: true });

      callModeActiveRef.current = true;
      setIsCallModeActive(true);
    } catch (error) {
      console.error('[useTotalConnectStandaloneWindow] Failed to enable call mode:', error);
    }
  }, [isTauriDesktop, handleWindowAclError]);

  // ── disableCallMode ──
  const disableCallMode = useCallback(async () => {
    if (!isTauriDesktop) return;
    if (!callModeActiveRef.current) return;

    try {
      if (!windowControlsBlockedRef.current) {
        const tauriWindow = await getCurrentTauriWindow();
        try {
          await tauriWindow.setAlwaysOnTop(false);
          setDesktopWindow((prev) => ({ ...prev, isAlwaysOnTop: false }));
        } catch (error) {
          if (!handleWindowAclError('disableCallMode', error)) throw error;
        }
      }

      await tauriInvoke('prevent_display_sleep', { prevent: false });

      callModeActiveRef.current = false;
      setIsCallModeActive(false);
    } catch (error) {
      console.error('[useTotalConnectStandaloneWindow] Failed to disable call mode:', error);
    }
  }, [isTauriDesktop, handleWindowAclError]);

  // ── focusWindow ──
  const focusWindow = useCallback(async () => {
    if (!isTauriDesktop) return;
    if (windowControlsBlockedRef.current) return;

    try {
      const tauriWindow = await getCurrentTauriWindow();
      try {
        await tauriWindow.show();
        await tauriWindow.unminimize();
        await tauriWindow.setFocus();
        setDesktopWindow((prev) => ({ ...prev, isMinimized: false, isFocused: true }));
      } catch (error) {
        if (handleWindowAclError('focusWindow', error)) return;
        throw error;
      }
    } catch (error) {
      console.error('[useTotalConnectStandaloneWindow] Failed to focus window:', error);
    }
  }, [isTauriDesktop, handleWindowAclError]);

  // ── refreshMediaDevices ──
  const refreshMediaDevices = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
      return;
    }

    setMediaDevicesLoading(true);
    try {
      // Enumerate devices without requesting getUserMedia — avoids triggering
      // camera/microphone permission prompts on page load. Device labels will
      // be empty until the user grants permission during an actual call.
      const devices = await navigator.mediaDevices.enumerateDevices();

      const audioInputs = devices.filter((d) => d.kind === 'audioinput');
      const audioOutputs = devices.filter((d) => d.kind === 'audiooutput');
      const videoInputs = devices.filter((d) => d.kind === 'videoinput');

      // Restore persisted device selections
      let selectedAudioInput: string | null = null;
      let selectedAudioOutput: string | null = null;
      let selectedVideoInput: string | null = null;

      try {
        const stored = localStorage.getItem('tc_media_device_prefs');
        if (stored) {
          const prefs = JSON.parse(stored);
          if (prefs.audioInput && audioInputs.some((d) => d.deviceId === prefs.audioInput)) {
            selectedAudioInput = prefs.audioInput;
          }
          if (prefs.audioOutput && audioOutputs.some((d) => d.deviceId === prefs.audioOutput)) {
            selectedAudioOutput = prefs.audioOutput;
          }
          if (prefs.videoInput && videoInputs.some((d) => d.deviceId === prefs.videoInput)) {
            selectedVideoInput = prefs.videoInput;
          }
        }
      } catch {
        // ignore
      }

      // Default to first available if no persisted selection
      if (!selectedAudioInput && audioInputs.length > 0) {
        selectedAudioInput = audioInputs[0].deviceId;
      }
      if (!selectedAudioOutput && audioOutputs.length > 0) {
        selectedAudioOutput = audioOutputs[0].deviceId;
      }
      if (!selectedVideoInput && videoInputs.length > 0) {
        selectedVideoInput = videoInputs[0].deviceId;
      }

      setMediaDevices({
        audioInputs,
        audioOutputs,
        videoInputs,
        selectedAudioInput,
        selectedAudioOutput,
        selectedVideoInput,
      });
    } catch (error) {
      console.error('[useTotalConnectStandaloneWindow] Device enumeration failed:', error);
    } finally {
      setMediaDevicesLoading(false);
    }
  }, []);

  // ── setAudioInputDevice ──
  const setAudioInputDevice = useCallback(async (deviceId: string) => {
    setMediaDevices((prev) => ({ ...prev, selectedAudioInput: deviceId }));
    try {
      const prefs = JSON.parse(localStorage.getItem('tc_media_device_prefs') || '{}');
      prefs.audioInput = deviceId;
      localStorage.setItem('tc_media_device_prefs', JSON.stringify(prefs));
    } catch {
      // ignore
    }
  }, []);

  // ── setAudioOutputDevice ──
  const setAudioOutputDevice = useCallback(async (deviceId: string) => {
    setMediaDevices((prev) => ({ ...prev, selectedAudioOutput: deviceId }));
    try {
      const prefs = JSON.parse(localStorage.getItem('tc_media_device_prefs') || '{}');
      prefs.audioOutput = deviceId;
      localStorage.setItem('tc_media_device_prefs', JSON.stringify(prefs));
    } catch {
      // ignore
    }
  }, []);

  // ── checkSipRegistration ──
  const checkSipRegistration = useCallback(async () => {
    setSipRegistrationLoading(true);
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        setSipRegistration((prev) => ({
          ...prev,
          status: 'unregistered',
          error: 'Not authenticated',
        }));
        return;
      }

      const res = await fetch(buildApiUrl('/api/v1/sip'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'getRegistrationStatus' }),
      });

      const data = await res.json();

      if (data.success && data.data) {
        setSipRegistration({
          status: data.data.registered ? 'registered' : 'unregistered',
          extension: data.data.extension || null,
          server: data.data.server || null,
          lastRegistered: data.data.last_registered ? Date.now() : null,
          error: null,
        });
      } else {
        setSipRegistration((prev) => ({
          ...prev,
          status: 'failed',
          error: data.error || 'Failed to check SIP registration',
        }));
      }
    } catch (error) {
      setSipRegistration((prev) => ({
        ...prev,
        status: 'failed',
        error: getErrorMessage(error) || 'Network error checking SIP registration',
      }));
    } finally {
      setSipRegistrationLoading(false);
    }
  }, []);

  // ── stopConnectionMonitoring ──
  const stopConnectionMonitoring = useCallback(() => {
    if (connectionMonitorRef.current) {
      clearInterval(connectionMonitorRef.current);
      connectionMonitorRef.current = null;
    }
    pcRef.current = null;
    setConnectionStats({
      quality: 'unknown',
      latencyMs: null,
      jitterMs: null,
      packetLossPercent: null,
      bandwidthKbps: null,
      iceConnectionState: null,
      lastUpdated: null,
    });
  }, []);

  // ── startConnectionMonitoring ──
  const startConnectionMonitoring = useCallback((pc: RTCPeerConnection) => {
    stopConnectionMonitoring();
    pcRef.current = pc;

    const poll = async () => {
      if (!pcRef.current || pcRef.current.connectionState === 'closed') {
        stopConnectionMonitoring();
        return;
      }

      try {
        const stats = await pcRef.current.getStats();
        let latencyMs = 0;
        let jitterMs = 0;
        let packetLossPercent = 0;
        let bandwidthKbps = 0;
        const iceState: RTCIceConnectionState | null = pcRef.current.iceConnectionState;

        let candidatePairFound = false;

        stats.forEach((report) => {
          if (report.type === 'candidate-pair' && report.state === 'succeeded') {
            candidatePairFound = true;
            latencyMs = Math.round((report.currentRoundTripTime || 0) * 1000);
          }

          if (report.type === 'inbound-rtp' && report.kind === 'audio') {
            jitterMs = Math.round((report.jitter || 0) * 1000);
            const packetsLost = report.packetsLost || 0;
            const packetsReceived = report.packetsReceived || 0;
            const total = packetsLost + packetsReceived;
            if (total > 0) {
              packetLossPercent = Math.round((packetsLost / total) * 10000) / 100;
            }
          }

          if (
            report.type === 'inbound-rtp' &&
            report.kind === 'audio' &&
            report.bytesReceived &&
            report.timestamp
          ) {
            bandwidthKbps = Math.round(
              ((report.bytesReceived || 0) * 8) / 1024 / 5,
            );
          }
        });

        const quality = candidatePairFound
          ? analyzeConnectionQuality({ latencyMs, jitterMs, packetLossPercent, bandwidthKbps })
          : 'unknown';

        setConnectionStats({
          quality,
          latencyMs: candidatePairFound ? latencyMs : null,
          jitterMs,
          packetLossPercent,
          bandwidthKbps,
          iceConnectionState: iceState,
          lastUpdated: Date.now(),
        });
      } catch {
        // Stats collection failed silently
      }
    };

    // Initial poll
    void poll();
    // Poll every 2 seconds
    connectionMonitorRef.current = setInterval(poll, 2000);
  }, [stopConnectionMonitoring]);

  // ── monitorLiveKitRoom ──
  const monitorLiveKitRoom = useCallback(
    (room: { name: string; state: string; numParticipants: number; serverUrl?: string }, error?: string | null) => {
      const statusMap: Record<string, LiveKitRoomStatus> = {
        disconnected: 'disconnected',
        connecting: 'connecting',
        connected: 'connected',
        reconnecting: 'reconnecting',
      };
      setLivekitRoom({
        status: statusMap[room.state] || 'unknown',
        roomName: room.name,
        participantCount: room.numParticipants,
        serverUrl: room.serverUrl || null,
        error: error || null,
      });
    },
    [],
  );

  // ── updateSipChannel ──
  const updateSipChannel = useCallback((status: Partial<SipChannelStatus>) => {
    setSipChannel((prev) => ({ ...prev, ...status }));
  }, []);

  // ── setPresence ──
  const setPresence = useCallback((status: PresenceStatus) => {
    setPresenceState(status);
    try {
      localStorage.setItem('tc_presence', status);
    } catch {
      // ignore
    }
  }, []);

  // ── broadcastWindowEvent ──
  const broadcastWindowEvent = useCallback(
    async (type: MultiWindowEvent['type'], payload: Record<string, unknown> = {}) => {
      if (!isTauriDesktop) return;
      try {
        const { emit } = await import('@tauri-apps/api/event');
        await emit('total-connect-event', {
          type,
          payload,
          sourceWindow: windowLabel || 'unknown',
          timestamp: Date.now(),
        });
      } catch {
        // Event bus unavailable
      }
    },
    [isTauriDesktop, windowLabel],
  );

  // ── setAudioOutputSink ──
  const setAudioOutputSink = useCallback(async (deviceId: string) => {
    setMediaDevices((prev) => ({ ...prev, selectedAudioOutput: deviceId }));
    try {
      const prefs = JSON.parse(localStorage.getItem('tc_media_device_prefs') || '{}');
      prefs.audioOutput = deviceId;
      localStorage.setItem('tc_media_device_prefs', JSON.stringify(prefs));
    } catch {
      // ignore
    }
    // Apply sinkId to any active audio elements
    if (typeof document !== 'undefined') {
      const audioElements = document.querySelectorAll('audio');
      audioElements.forEach((el) => {
        if ('setSinkId' in el && typeof (el as HTMLAudioElement & { setSinkId: (id: string) => Promise<void> }).setSinkId === 'function') {
          (el as HTMLAudioElement & { setSinkId: (id: string) => Promise<void> }).setSinkId(deviceId).catch(() => {});
        }
      });
    }
  }, []);

  // ── Restore persisted presence on mount ──
  useEffect(() => {
    try {
      const stored = localStorage.getItem('tc_presence');
      if (stored && ['online', 'away', 'busy', 'dnd', 'offline'].includes(stored)) {
        setPresenceState(stored as PresenceStatus);
      }
    } catch {
      // ignore
    }
  }, []);

  // ── Auto-refresh media devices on mount ──
  useEffect(() => {
    void refreshMediaDevices();

    // Listen for device changes
    if (typeof navigator !== 'undefined' && navigator.mediaDevices?.addEventListener) {
      const handler = () => {
        void refreshMediaDevices();
      };
      navigator.mediaDevices.addEventListener('devicechange', handler);
      return () => {
        navigator.mediaDevices.removeEventListener('devicechange', handler);
      };
    }
  }, [refreshMediaDevices]);

  // ── Auto-check SIP registration on mount (Tauri only) ──
  useEffect(() => {
    if (isTauriDesktop) {
      void checkSipRegistration();
    }
  }, [isTauriDesktop, checkSipRegistration]);

  // ── Cleanup on unmount ──
  useEffect(() => {
    return () => {
      stopConnectionMonitoring();
      if (callModeActiveRef.current && isTauriDesktop) {
        if (!windowControlsBlockedRef.current) {
          getCurrentTauriWindow()
            .then((w) => w.setAlwaysOnTop(false))
            .catch(() => {});
        }
        tauriInvoke('prevent_display_sleep', { prevent: false }).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isStandalone,
    windowLabel,
    isTauriDesktop,
    isCapacitorMobile,
    isWebBrowser,
    platform,

    sipRegistration,
    sipRegistrationLoading,

    mediaDevices,
    mediaDevicesLoading,

    connectionStats,

    desktopWindow,

    isCallModeActive,

    livekitRoom,
    sipChannel,
    presence,

    enableCallMode,
    disableCallMode,
    focusWindow,
    refreshMediaDevices,
    setAudioInputDevice,
    setAudioOutputDevice,
    checkSipRegistration,
    startConnectionMonitoring,
    stopConnectionMonitoring,
    monitorLiveKitRoom,
    updateSipChannel,
    setPresence,
    broadcastWindowEvent,
    setAudioOutputSink,
  };
}
